import { gsap } from "gsap";

function parseCountString(raw) {
    // Ex: "+65", "+15 ano", "+5 estados", "12,5%", "1.200", "1.200,50"
    const str = String(raw ?? "").trim();
    if (!str) return null;

    // pega a primeira ocorrência de número (com . ou ,)
    const match = str.match(/-?\d[\d.,]*/);
    if (!match) return null;

    const numToken = match[0];

    // normaliza pra float:
    // - se tiver vírgula e ponto, assume "." milhar e "," decimal (pt-BR): "1.200,50"
    // - se só tiver vírgula, assume decimal: "12,5"
    let normalized = numToken;

    const hasDot = normalized.includes(".");
    const hasComma = normalized.includes(",");

    if (hasDot && hasComma) {
        normalized = normalized.replace(/\./g, "").replace(",", ".");
    } else if (hasComma && !hasDot) {
        normalized = normalized.replace(",", ".");
    }

    const end = Number.parseFloat(normalized);
    if (!Number.isFinite(end)) return null;

    const startIndex = match.index ?? 0;
    const endIndex = startIndex + numToken.length;

    const prefix = str.slice(0, startIndex);
    const suffix = str.slice(endIndex);

    // define casas decimais pelo token original
    const decimals = (normalized.split(".")[1] || "").length;

    return { end, prefix, suffix, decimals };
}

function formatNumber(value, decimals) {
    // formata com separador BR (opcional). Se não quiser, use value.toFixed(decimals)
    return value.toLocaleString("pt-BR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

export default function initCountUp(root = document) {
    const els = Array.from(root.querySelectorAll(".js-countup[data-count]"));
    if (!els.length) return;

    const reduceMotion =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const run = (el) => {
        if (el.dataset.countupDone === "1") return;

        const parsed = parseCountString(el.dataset.count);
        if (!parsed) return;

        el.dataset.countupDone = "1";

        if (reduceMotion) {
            el.textContent = `${parsed.prefix}${formatNumber(parsed.end, parsed.decimals)}${parsed.suffix}`;
            return;
        }

        const obj = { val: 0 };

        // começa visualmente do 0 (mantendo prefix/suffix)
        el.textContent = `${parsed.prefix}${formatNumber(0, parsed.decimals)}${parsed.suffix}`;

        gsap.to(obj, {
            val: parsed.end,
            duration: 1.1,
            ease: "power2.out",
            onUpdate: () => {
                // snap evita ficar mostrando 3.999999
                const current =
                    parsed.decimals > 0
                        ? Number(obj.val.toFixed(parsed.decimals))
                        : Math.round(obj.val);

                el.textContent = `${parsed.prefix}${formatNumber(current, parsed.decimals)}${parsed.suffix}`;
            },
            onComplete: () => {
                el.textContent = `${parsed.prefix}${formatNumber(parsed.end, parsed.decimals)}${parsed.suffix}`;
            },
        });
    };

    // Observa o container dos stats (melhor performance)
    const containers = new Set(
        els.map((el) => el.closest(".home-hero__stats")).filter(Boolean),
    );

    const io = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                // anima todos os countups dentro do container que entrou
                const inner = entry.target.querySelectorAll(
                    ".js-countup[data-count]",
                );
                inner.forEach(run);

                // só uma vez
                io.unobserve(entry.target);
            });
        },
        { threshold: 0.35 },
    );

    containers.forEach((c) => io.observe(c));

    // fallback: se não achar container, observa cada elemento
    if (!containers.size) {
        const io2 = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    run(entry.target);
                    io2.unobserve(entry.target);
                });
            },
            { threshold: 0.6 },
        );
        els.forEach((el) => io2.observe(el));
    }
}
